<template>
  <FormWrapper :handleSubmit="submit" :loading="submitLoading">
    <template #default>
      <FormLoading :loading="loading" :count="8">
        <b-row class="margin-0">
          <b-col cols="12" class="padding-0">
            <EnumDropdown
              :title="$lang.status"
              :label="$lang.status"
              :placeholder="$lang.status"
              :infoTooltip="$lang.termStatusTooltip"
              :value="form.status"
              name="status"
              id="status"
              categoryName="term_status"
              :vuelidateObj="$v.form.status"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <Dropdown
              :title="$lang.name_company"
              :label="$lang.name_company"
              :placeholder="$lang.name_company"
              :infoTooltip="$lang.select_company_tooltip"
              :value="form.companyName"
              name="companyName"
              id="companyName"
              :vuelidateObj="$v.form.companyName"
              :options="companyNameOptions"
              :loading="companyNameLoading"
              :asyncSearch="getCompanyNameOptions"
              :disabled="true"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <Input
              :title="$lang.term_title"
              :label="$lang.term_title"
              :placeholder="$lang.term_title"
              :value="form.termTitle"
              name="termTitle"
              id="termTitle"
              type="text"
              :vuelidateObj="$v.form.termTitle"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col
            cols="12"
            sm="6"
            md="6"
            class="padding-0 padding-left-8 mobile-padding-0"
          >
            <DatePicker
              :title="$lang.start_datetime"
              :label="$lang.start_datetime"
              :placeholder="$lang.start_datetime"
              :value="form.startDate"
              name="startDate"
              id="startDate"
              rightIcon="calendar-clock"
              displayFormat="HH:mm jYYYY/jMM/jDD"
              format="YYYY-MM-DD HH:mm:ss"
              type="datetime"
              :popover="true"
              :simple="true"
              :autoSubmit="true"
              :hasEraseIcon="true"
              :vuelidateObj="$v.form.startDate"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
              @erase="eraseDatePicker"
            />
          </b-col>
          <b-col
            cols="12"
            sm="6"
            md="6"
            class="padding-0 padding-right-8 mobile-padding-0"
          >
            <DatePicker
              :title="$lang.end_datetime"
              :label="$lang.end_datetime"
              :placeholder="$lang.end_datetime"
              :value="form.endDate"
              name="endDate"
              id="endDate"
              rightIcon="calendar-clock"
              displayFormat="HH:mm jYYYY/jMM/jDD"
              format="YYYY-MM-DD HH:mm:ss"
              type="datetime"
              :popover="true"
              :simple="true"
              :autoSubmit="true"
              :hasEraseIcon="true"
              :vuelidateObj="$v.form.endDate"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
              @erase="eraseDatePicker"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <Switchbox
              :label="$lang.checkTwoStepVerification"
              :infoTooltip="$lang.checkTwoStepVerificationTooltip"
              :value="form.checkTwoStepVerification"
              name="checkTwoStepVerification"
              boxClass="flex-between"
              @input="input"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <Switchbox
              :label="$lang.directLogin"
              :value="form.directLogin"
              name="directLogin"
              boxClass="flex-between"
              @input="input"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <Dropdown
              :title="$lang.termType"
              :label="$lang.termType"
              :placeholder="$lang.termType"
              :value="form.termType"
              name="termType"
              id="termType"
              :vuelidateObj="$v.form.termType"
              :options="termTypeOptions"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>

          <b-col v-if="isTermTypeForVolunteers" cols="12" class="padding-0">
            <EnumDropdown
              :title="$lang.licenseType"
              :label="$lang.licenseType"
              :placeholder="$lang.licenseType"
              :value="form.licenseType"
              name="licenseType"
              id="licenseType"
              categoryName="term_license"
              :multiple="false"
              :vuelidateObj="$v.form.licenseType"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col v-if="!isTermTypeForVolunteers" cols="12" class="padding-0">
            <EnumDropdown
              :title="$lang.cooperationType"
              :label="$lang.cooperationType"
              :placeholder="$lang.cooperationType"
              :value="form.cooperationType"
              name="cooperationType"
              id="cooperationType"
              categoryName="cooperation_kind"
              :multiple="false"
              :vuelidateObj="$v.form.cooperationType"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col v-if="isTermTypeForVolunteers" cols="12" class="padding-0">
            <Dropdown
              :title="`${$lang.limitRegistration} (${$lang.optional})`"
              :label="$lang.limitRegistration"
              :placeholder="$lang.limitRegistration"
              :infoTooltip="$lang.limitRegistrationTooltip"
              :value="form.limitRegistration"
              name="limitRegistration"
              id="limitRegistration"
              :multiple="true"
              :vuelidateObj="$v.form.limitRegistration"
              :options="limitRegistrationOptions"
              :loading="limitRegistrationLoading"
              :asyncSearch="getLimitRegistrationOptions"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <Dropdown
              :title="$lang.termParvandehDefaultRole"
              :label="$lang.termParvandehDefaultRole"
              :placeholder="$lang.termParvandehDefaultRole"
              :value="form.defaultRole"
              name="defaultRole"
              id="defaultRole"
              :vuelidateObj="$v.form.defaultRole"
              :options="defaultRoleOptions"
              :loading="defaultRoleLoading"
              :asyncSearch="getDefaultRoleOptions"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <Dropdown
              :title="`${$lang.azmoon} (${$lang.optional})`"
              :label="$lang.azmoon"
              :placeholder="$lang.azmoon"
              :value="form.azmoon"
              name="azmoon"
              id="azmoon"
              :multiple="true"
              :vuelidateObj="$v.form.azmoon"
              :options="azmoonOptions"
              :loading="azmoonLoading"
              :asyncSearch="getAzmoonOptions"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col cols="12" class="padding-0" v-if="form.azmoon.length > 1">
            <EnumDropdown
              :title="$lang.selectAzmoonMethod"
              :label="$lang.selectAzmoonMethod"
              :placeholder="$lang.selectAzmoonMethod"
              :value="form.preRegisterKindSelectAzmoon"
              name="preRegisterKindSelectAzmoon"
              id="preRegisterKindSelectAzmoon"
              categoryName="pre_register_kind_select_azmoon_enum"
              :multiple="false"
              :vuelidateObj="$v.form.preRegisterKindSelectAzmoon"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
          <b-col cols="12" class="padding-0">
            <Input
              :title="`${$lang.description} (${$lang.optional}):`"
              :label="$lang.description"
              :placeholder="$lang.description"
              :value="form.description"
              name="description"
              id="description"
              type="textarea"
              :minLength="lengthNumber3"
              :maxLength="lengthNumber256"
              :vuelidateObj="$v.form.description"
              @input="input"
              @focus="resetValidation"
              @blur="checkValidation"
            />
          </b-col>
        </b-row>
      </FormLoading>
    </template>
  </FormWrapper>
</template>

<script>
import inputMixin from "@/mixin/inputMixin.vue";
import {
  FormWrapper,
  FormLoading,
  Dropdown,
  Input,
  DatePicker,
  Switchbox,
  EnumDropdown,
} from "@/components/re-design/forms";
import {
  required,
  requiredIf,
  minLength,
  maxLength,
} from "vuelidate/lib/validators";
import { pathOr } from "ramda";
import { checkPermission } from "@/shared/utils";
import {
  getAzmoonExecuteCombo,
  getCompanyCombo,
  getRoleCombo,
  getTermCombo,
} from "@/services";
import { editGeneralInformationTerm } from "@/modules/profile/services";
export default {
  mixins: [inputMixin],
  inject: ["TERM_CODE", "TERM_DATA"],
  components: {
    FormWrapper,
    FormLoading,
    Dropdown,
    Input,
    DatePicker,
    Switchbox,
    EnumDropdown,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
  },
  validations() {
    return {
      form: {
        status: { required },
        companyName: { required },
        termTitle: { required },
        startDate: { required },
        endDate: { required },
        termType: { required },
        licenseType: {
          required: requiredIf(() => this.isTermTypeForVolunteers),
        },
        cooperationType: {
          required: requiredIf(() => !this.isTermTypeForVolunteers),
        },
        limitRegistration: {},
        defaultRole: { required },
        azmoon: {},
        preRegisterKindSelectAzmoon: {},
        description: {
          minLength: minLength(this.lengthNumber3),
          maxLength: maxLength(this.lengthNumber256),
        },
      },
    };
  },
  data() {
    return {
      form: {
        status: null,
        companyName: null,
        termTitle: null,
        startDate: null,
        endDate: null,
        checkTwoStepVerification: false,
        directLogin: false,
        termType: null,
        licenseType: null,
        cooperationType: null,
        limitRegistration: [],
        defaultRole: null,
        azmoon: [],
        preRegisterKindSelectAzmoon: null,
        description: null,
      },
      submitLoading: false,
      companyNameLoading: false,
      companyNameOptions: [],
      limitRegistrationLoading: false,
      limitRegistrationOptions: [],
      azmoonLoading: false,
      azmoonOptions: [],
      defaultRoleLoading: false,
      defaultRoleOptions: [],
      termTypeOptions: [
        { id: 0, caption: "برای داوطلبان" },
        { id: 1, caption: "برای همکاران" },
      ],
    };
  },
  computed: {
    isTermTypeForVolunteers() {
      return pathOr(null, ["termType", "id"], this.form) === 0;
    },
  },
  watch: {
    TERM_DATA: {
      immediate: true,
      handler(value) {
        if (value) {
          this.fetchData(value);
        }
      },
    },
  },
  methods: {
    checkPermission,
    eraseDatePicker(key) {
      this.input(key, null);
    },
    async getCompanyNameOptions(text = null) {
      try {
        this.companyNameLoading = true;
        const response = await getCompanyCombo(text);
        const { options } = response;
        this.companyNameOptions = [...options];
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.companyNameLoading = false;
      }
    },
    async getLimitRegistrationOptions(text = null) {
      try {
        this.limitRegistrationLoading = true;
        const response = await getTermCombo(text);
        const { options } = response;
        this.limitRegistrationOptions = [...options];
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.limitRegistrationLoading = false;
      }
    },
    async getAzmoonOptions(text = null) {
      try {
        this.azmoonLoading = true;
        const response = await getAzmoonExecuteCombo(text);
        const { options } = response;
        this.azmoonOptions = [...options];
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.azmoonLoading = false;
      }
    },
    async getDefaultRoleOptions(text = null) {
      try {
        this.defaultRoleLoading = true;
        const response = await getRoleCombo(text);
        const { options } = response;
        this.defaultRoleOptions = [...options];
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.defaultRoleLoading = false;
      }
    },
    async submit() {
      this.$v.$touch();
      if (this.$v.$anyError) return;

      try {
        this.submitLoading = true;
        const {
          status,
          termTitle,
          startDate,
          endDate,
          checkTwoStepVerification,
          directLogin,
          termType,
          licenseType,
          cooperationType,
          limitRegistration,
          defaultRole,
          azmoon,
          preRegisterKindSelectAzmoon,
          description,
        } = this.form;

        const licenseId = this.isTermTypeForVolunteers
          ? pathOr(null, ["id"], licenseType)
          : null;
        const cooperationId = !this.isTermTypeForVolunteers
          ? pathOr(null, ["id"], cooperationType)
          : null;

        const payloadData = {
          termCode: this.TERM_CODE,
          status: pathOr(null, ["id"], status),
          termTitle,
          startDate,
          endDate,
          checkTwoStepVerification: Number(checkTwoStepVerification),
          directLogin: +directLogin,
          licenseType: licenseId,
          cooperationType: cooperationId,
          limitRegistration,
          defaultRole: pathOr(null, ["id"], defaultRole),
          azmoon: azmoon.map((item) => item.id),
          preRegisterKindSelectAzmoonId: pathOr(
            null,
            ["id"],
            preRegisterKindSelectAzmoon
          ),
          description,
        };

        const response = await editGeneralInformationTerm(payloadData);
        const { message } = response;
        this.$notify.success(message);
        this.$emit("getTermData");
      } catch (error) {
        this.$generateError(error);
      } finally {
        this.submitLoading = false;
      }
    },
    fetchData(data) {
      this.input("status", pathOr(null, ["status"], data));
      const companyName = {
        id: pathOr(null, ["company", "code"], data),
        caption: pathOr(null, ["company", "caption"], data),
      };
      this.input("companyName", companyName);
      this.input("termTitle", pathOr(null, ["caption"], data));
      this.input("startDate", pathOr(null, ["start_date", "gregorian"], data));
      this.input("endDate", pathOr(null, ["end_date", "gregorian"], data));
      this.input(
        "checkTwoStepVerification",
        pathOr(false, ["two_step_verification"], data)
      );
      this.input("directLogin", pathOr(false, ["direct_login"], data));
      const license = pathOr(null, ["license"], data);
      const termTypeId = license ? 0 : 1;
      const termType = this.termTypeOptions.find(
        (item) => item.id === termTypeId
      );
      this.input("termType", termType);
      this.input("licenseType", license);
      this.input("cooperationType", pathOr(null, ["cooperation"], data));
      this.input("defaultRole", {
        id: pathOr("", ["role", "code"], data),
        caption: pathOr("", ["role", "caption"], data),
      });
      this.input("limitRegistration", pathOr([], ["limit_registration"], data));
      this.input("azmoon", pathOr([], ["azmoon_id"], data));
      this.input(
        "preRegisterKindSelectAzmoon",
        pathOr(null, ["pre_register_kind_select_azmoon"], data)
      );
      this.input("description", pathOr("", ["description"], data));
    },
  },
};
</script>
